import React, { useContext, ChangeEvent } from "react";

import * as S from "@styled";
import Select, { SingleSelectProps, MultiSelect, MultiSelectProps } from "shared/components/select/Select";
import { Option } from "shared/model/Option";
import { selectWithInput } from "shared/components/select/model";
import { ISearchInputProps } from "shared/styledComponents/Input";

import { AppContext } from "../App";
import { ProcessTemplateAppReducerState } from "processTemplates/model/AppState";
import { StylesConfig } from "react-select";
import { Utilities } from "shared/utilities";
import { ProcessTemplateFilterType } from "processTemplates/model/List";

interface IFiltersProps {
    filter: ProcessTemplateFilter;
    onChange: (newValue: ProcessTemplateFilter) => void;
}

const getOption = (value: string) => ({ label: value, value });

const Filters: React.FC<IFiltersProps> = ({ filter, onChange }) => {
    const { state } = useContext<ProcessTemplateAppReducerState>(AppContext);

    const filterTypeOptions = Object.values(ProcessTemplateFilterType).map(getOption);

    const handleFilterTypeChange = (newValue: Option) => {
        onChange({ type: newValue.value as ProcessTemplateFilterType, values: [] });
    };

    const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange({ ...filter, values: [event.currentTarget.value] });
    };

    const handleMultiSelectChange = (newValues: Option[]) => {
        onChange({ ...filter, values: newValues.length ? newValues.map((v) => v.value) : [] })
    };

    return (
        <S.FlexContainer
            alignItemsEnd
            style={{ marginTop: '4px', flexWrap: 'nowrap' }}
        >
            <S.Label style={{ display: 'grid', gap: '5px', marginBottom: '0' }}>
                <span>
                    Filter by
                </span>

                <FilterSelect
                    id="filter-category-selector"
                    options={filterTypeOptions}
                    value={Utilities.valueFromId(filterTypeOptions, filter.type)}
                    onChange={handleFilterTypeChange}
                />
            </S.Label>

            {filter.type === ProcessTemplateFilterType.Text && (
                <S.Label style={{ marginBottom: '0' }}>
                    <S.SROnly>
                        Search
                    </S.SROnly>

                    <FilterSearchInput
                        id="filter-text-selector"
                        value={filter.values.length ? filter.values[0] : ''}
                        onChange={handleSearchInputChange}
                    />
                </S.Label>
            )}

            {filter.type === ProcessTemplateFilterType.Tags && (
                <S.Label style={{ marginBottom: '0' }}>
                    <S.SROnly>
                        Tags
                    </S.SROnly>

                    <FilterMultiSelect
                        id="filter-tags-selector"
                        options={state.availableTags.map(getOption)}
                        value={filter.values.filter((v) => v !== '').map(getOption)}
                        onChange={handleMultiSelectChange}
                    />
                </S.Label>
            )}
        </S.FlexContainer>
    );
};

export default Filters;

export interface ProcessTemplateFilter {
    type: ProcessTemplateFilterType;
    values: string[];
}

const FilterSelect: React.FC<SingleSelectProps> = (props) => {
    return (
        <Select
            {...props}
            classNamePrefix="react-select"
            styles={selectWithInput}
            height="initial"
            borderRadius="25px 0px 0px 25px"
            borderWidthRight="0px !important"
        />
    );
};

const FilterSearchInput: React.FC<ISearchInputProps> = (props) => {
    return (
        <S.SearchInput
            {...props}
            placeholder={FRENDS.Localizations.FilterByTextAndDescription}
            style={{ width: "405px", height: '44px', borderRadius: "0px 25px 25px 0px" }}
            isFullWidth
        />
    );
};

const FilterMultiSelect: React.FC<MultiSelectProps> = (props) => {
    return (
        <MultiSelect
            {...props}
            classNamePrefix="react-select"
            styles={customFilterSelectStyles}
            placeholder={FRENDS.Localizations.Select}
            autoFocus={true}
            borderRadius="0px 25px 25px 0px"
            height="initial"
        />
    );
};

const customFilterSelectStyles: StylesConfig<Option> = ({
    ...selectWithInput,
    container: (provided) => ({ ...provided, width: 405 })
});